import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { LateFeesSettingsRepository } from '~/repositories/settings/lateFeesSettingsRepository';
import type { Accessor } from 'solid-js';
import type { LateFeesSettings, LateFeesSettingsCompanyPortalTypedSettingsWithOverridesDocument } from '~/swagger/settings/Api';

interface LateFeesSettingsContextValue {
  addLateFeesSettings: (payload: LateFeesSettings) => Promise<void>;
  addLateFeesSettingsByLevel: (level: string, levelId: string, payload: LateFeesSettings) => Promise<void>;
  getLateFeesSettings: () => Promise<LateFeesSettings | undefined>;
  getLateFeesSettingsByLevel: (level: string, levelId: string) => Promise<LateFeesSettings | undefined>;
  getAllLateFeesSettings: () => Promise<LateFeesSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined>;
  isLoading: Accessor<boolean>;
  error: Accessor<Error | undefined>;
  lateFeesSettingsWithNoOverrides: Accessor<LateFeesSettings | undefined>;
  allLateFeesSettings: Accessor<LateFeesSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined>;
  deleteLateFeesSettingsByLevel: (level: string, levelId: string) => Promise<void>;
}

export const [LateFeesSettingsProvider, useLateFeesSettings] = createMagicDoorContext<LateFeesSettingsContextValue>(
  'LateFeesSettings',
  () => {
    const repo = new LateFeesSettingsRepository();
    const [lateFeesSettingsWithNoOverrides, setLateFeesSettingsWithNoOverrides] = createSignal<LateFeesSettings | undefined>(undefined);
    const [allLateFeesSettings, setAllLateFeesSettings] = createSignal<
      LateFeesSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
    >(undefined);
    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);

    const addLateFeesSettings = async (payload: LateFeesSettings) => {
      setIsLoading(true);
      try {
        await repo.addLateFeesSettings(payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const addLateFeesSettingsByLevel = async (level: string, levelId: string, payload: LateFeesSettings) => {
      setIsLoading(true);
      try {
        await repo.addLateFeesSettingsByLevel(level, levelId, payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const getLateFeesSettings = async (): Promise<LateFeesSettings | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getLateFeesSettings();
        setLateFeesSettingsWithNoOverrides(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getLateFeesSettingsByLevel = async (level: string, levelId: string): Promise<LateFeesSettings | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getLateFeesSettingsByLevel(level, levelId);
        setLateFeesSettingsWithNoOverrides(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const getAllLateFeesSettings = async (): Promise<LateFeesSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined> => {
      setIsLoading(true);
      try {
        const result = await repo.getAllLateFeesSettings();
        setAllLateFeesSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const deleteLateFeesSettingsByLevel = async (level: string, levelId: string) => {
      setIsLoading(true);
      try {
        await repo.deleteLateFeesSettingsByLevel(level, levelId);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    return {
      deleteLateFeesSettingsByLevel,
      addLateFeesSettings,
      addLateFeesSettingsByLevel,
      getLateFeesSettings,
      getLateFeesSettingsByLevel,
      getAllLateFeesSettings,
      allLateFeesSettings,
      isLoading,
      lateFeesSettingsWithNoOverrides,
      error,
    };
  }
);
